import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CatalogueApiService,
  type ProviderFeedbackItem,
} from '@angular-workspace/services';
import { AdditionalInfoComponent } from './additional-info.component';
import { AdditionalInfoEditComponent } from './additional-info-edit.component';
import {
  type AdditionalInfoFeedbackItem,
  type AdditionalInfoFaqRow,
  hasFaqOrInfoFeedback,
  splitFeedbackItems,
} from './additional-info.logic';

@Component({
  selector: 'lib-additional-info-modal',
  standalone: true,
  imports: [CommonModule, AdditionalInfoComponent, AdditionalInfoEditComponent],
  templateUrl: './additional-info-modal.component.html',
  styleUrl: './additional-info-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalInfoModalComponent implements OnInit {
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly addRef = viewChild(AdditionalInfoComponent);
  private readonly editRef = viewChild(AdditionalInfoEditComponent);

  readonly assetId = input.required<string>();
  readonly assetLabel = input('');
  readonly assetKindLabel = input('Dataset');
  readonly preloadedItems = input<ProviderFeedbackItem[] | null>(null);

  readonly closed = output<void>();
  readonly saved = output<ProviderFeedbackItem[]>();

  loading = true;
  submitting = false;
  isEdit = false;
  editFaqRows: AdditionalInfoFaqRow[] = [];
  editInfoText = '';
  private loadedItems: ProviderFeedbackItem[] = [];

  ngOnInit(): void {
    const preloaded = this.preloadedItems();
    if (preloaded !== null) {
      this.applyItems(preloaded);
      return;
    }
    void this.fetchItems();
  }

  onClose(): void {
    if (this.submitting) {
      return;
    }
    this.closed.emit();
  }

  async onSubmit(): Promise<void> {
    if (this.submitting || this.loading) {
      return;
    }
    const items = this.collectItemsToPost();
    if (items === null) {
      return;
    }
    if (items.length === 0) {
      this.closed.emit();
      return;
    }

    this.submitting = true;
    this.cdr.markForCheck();
    const assetId = this.assetId();
    for (const item of items) {
      const payload = { assetId, type: item.type, data: item.data };
      try {
        if (this.isEdit) {
          await this.catalogueApi.putProviderFeedback(payload);
        } else {
          await this.catalogueApi.postProviderFeedback(payload);
        }
      } catch (error) {
        console.error('Failed to submit additional info item:', error);
      }
    }
    const refreshed = await this.catalogueApi.getProviderFeedback(assetId);
    if (refreshed.length > 0) {
      this.saved.emit(refreshed);
    } else {
      const optimistic: ProviderFeedbackItem[] = items.map((item, i) => ({
        id: `local-${i}`,
        userId: '',
        assetId,
        type: item.type,
        data: item.data,
        createdAt: new Date().toISOString(),
      }));
      const kept = this.isEdit
        ? this.loadedItems.filter((loaded) => !items.some((item) => item.type === loaded.type))
        : this.loadedItems;
      this.saved.emit([...kept, ...optimistic]);
    }
    this.submitting = false;
    this.cdr.markForCheck();
  }

  private async fetchItems(): Promise<void> {
    this.loading = true;
    this.cdr.markForCheck();
    const items = await this.catalogueApi.getProviderFeedback(this.assetId());
    this.applyItems(items);
  }

  private applyItems(items: ProviderFeedbackItem[]): void {
    this.loadedItems = items ?? [];
    const split = splitFeedbackItems(this.loadedItems);
    this.editFaqRows = split.faqRows;
    this.editInfoText = split.infoText;
    this.isEdit = hasFaqOrInfoFeedback(this.loadedItems);
    this.loading = false;
    this.cdr.markForCheck();
  }

  get submitLabel(): string {
    if (this.isEdit) {
      return this.submitting ? 'Saving…' : 'Save Changes';
    }
    return this.submitting ? 'Submitting…' : 'Submit';
  }

  /** Modify mode: disabled until the edit form actually differs from what was loaded. */
  get canSubmit(): boolean {
    if (this.loading || this.submitting) {
      return false;
    }
    return this.isEdit ? (this.editRef()?.hasChanges ?? false) : true;
  }

  private collectItemsToPost(): AdditionalInfoFeedbackItem[] | null {
    if (this.isEdit) {
      const edit = this.editRef();
      if (!edit?.validate()) {
        return null;
      }
      return edit.getChangedFeedbackItems();
    }
    const add = this.addRef();
    if (!add?.validate()) {
      return null;
    }
    return add.getFeedbackItems();
  }
}
